import React, { Component } from 'react';
import classNames from 'classnames';
import { Link } from 'react-router-dom';
import Table from '@common/component/atoms/Table';
import HorizontalScrollArea from '@common/component/atoms/HorizontalScrollArea';
import { TableDataProps } from '@common/utils/NegotiationReducerUtils';
import { TableRowTitleProps } from '@common/redux/reducers/ReportTableRowReducer';
import LinearProgress from '@common/component/atoms/LinearProgress';
import ButtonGroup from '@common/component/atoms/ButtonGroup';
import Card from '@common/component/atoms/Card';
import { Icon } from '@material-ui/core';
import { ACTION_TYPE, GASpotEventMappingState, REPORT_SWITCH_TAB_CLASSNAME, SCREEN, TAB_TYPE } from '@common/Common';

export interface ReportTableRow {
  tableRowTitle: TableRowTitleProps;
  displayKeyList: string[];
}

export interface tabContent {
  tabkey: {
    title: string;
    lineType: string;
    type: string;
    url: string;
  };
  tableCount: TableDataProps;
  gaSpotEvent?: {
    [key: string]: GASpotEventMappingState | { [subKey: string]: GASpotEventMappingState };
  };
}

export interface CountTableProps {
  isLoading: boolean;
  tabContent: tabContent;
  reportTableRow: ReportTableRow;
  headerStatus: string;
  updateSearchCondition: (tabType: string, rowKey: string, colKey: string) => void;
}

export interface CountTableState {
  dispType: 'total' | 'customers';
  isPendingNegotiationActionTab: boolean;
}

class CountTable extends Component<CountTableProps, CountTableState> {
  constructor(props: CountTableProps) {
    super(props);

    this.state = {
      dispType: 'total',
      isPendingNegotiationActionTab: props.tabContent.tabkey.url === `${SCREEN.PENDING_NEGOTIATION.URL}/${TAB_TYPE.ACTION}`,
    };
  }

  componentDidMount() {
    Object.keys(REPORT_SWITCH_TAB_CLASSNAME).forEach((tabName: string) => {
      const gaSpotEventInfo = this.props.tabContent?.gaSpotEvent?.[tabName];
      if (!gaSpotEventInfo) return;
      document.querySelector(`.${gaSpotEventInfo.className}`)?.setAttribute('onclick', `${gaSpotEventInfo.spotEvent}`);
    });
  }

  render() {
    return (
      <div className='container u-h-full u-flex u-flex-column'>
        <div className='u-p8 u-pt0 u-flex u-space-between'>
          <div style={{ paddingTop: '6px' }}>
            <h1 className='headline-3' data-testid-e2e='countTable-title'>{`${this.props.tabContent.tabkey.title}の合計`}</h1>
          </div>
          <div>
            <span className='u-mr16'>表示の切り替え</span>
            <ButtonGroup>
              <ButtonGroup.Button
                isActive={this.state.dispType === 'total'}
                label='商談数'
                onClick={() => this.setState({ dispType: 'total' })}
                data-testid='disp-negotiation'
                data-testid-e2e='countTable-dispNegotiation'
                className={REPORT_SWITCH_TAB_CLASSNAME.countTableDispNegotiation}
              />
              <ButtonGroup.Button
                isActive={this.state.dispType === 'customers'}
                label='顧客数'
                onClick={() => this.setState({ dispType: 'customers' })}
                data-testid='disp-customer'
                data-testid-e2e='countTable-dispCustomer'
                className={REPORT_SWITCH_TAB_CLASSNAME.countTableDispCustomer}
              />
            </ButtonGroup>
          </div>
        </div>
        <HorizontalScrollArea>
          <div className='report-cards'>
            {this.props.tabContent.tableCount.data[this.state.dispType].map((tableCol: number, i: number) => {
              if (this.state.isPendingNegotiationActionTab && this.props.tabContent.tableCount.header[i] === ACTION_TYPE.NOT_ENTERED)
                return null;
              return (
                <Link
                  className='report-cards__item'
                  key={`total-${i + 2}`}
                  to={SCREEN.NEGOTIATION_SEARCH_RESULT.URL}
                  onClick={() =>
                    this.props.updateSearchCondition(
                      this.props.tabContent.tabkey.lineType,
                      this.state.dispType,
                      this.props.tabContent.tableCount.header[i]
                    )
                  }
                  style={{ pointerEvents: tableCol === 0 ? 'none' : 'auto' }}
                  data-testid='report-cards-item'>
                  <Card
                    className={classNames(
                      'report-cards__item-card',
                      `is-${this.props.tabContent.tabkey.type}-${this.props.tabContent.tableCount.header[i]}`
                    )}>
                    <Card.Content className='report-cards__item-card-content'>
                      <div className='report-cards__item-left'>
                        <p className='report-cards__item-title' data-testid='report-cards-item-title'>
                          {this.props.tabContent.tableCount.headerDisp[this.props.tabContent.tableCount.header[i]]}
                        </p>
                        <p
                          className='report-cards__item-value'
                          data-testid='report-cards-item-value'
                          data-testid-e2e={`countTable-reportCardsItem_${i}`}>
                          {tableCol}
                        </p>
                      </div>
                      <div className='report-cards__item-right'>
                        <Icon className='report-cards__item-right-icon'>keyboard_arrow_right</Icon>
                      </div>
                    </Card.Content>
                  </Card>
                </Link>
              );
            })}
          </div>
        </HorizontalScrollArea>
        <div className='u-ph8 u-pb8 u-pt24'>
          <h1 className='headline-3'>すべてのデータ（商談数で表示されます）</h1>
        </div>
        <Table className='report-table u-flex-1 u-overflow-hidden' isScrollable={true} height='s'>
          <Table.THead>
            <Table.Tr className='report-table__header-row'>
              {this.props.reportTableRow.tableRowTitle.header.map((colTitle: string, i: number) => (
                <Table.Th
                  className={classNames('u-min-ws', 'u-bg-gray-alt', 'report-table__col-sticky', `report-table__col-sticky--${i + 1}`)}
                  key={colTitle}>
                  {colTitle}
                </Table.Th>
              ))}
              {this.props.tabContent.tableCount.header.map((key: string) => {
                if (this.state.isPendingNegotiationActionTab && key === ACTION_TYPE.NOT_ENTERED) return null;
                return (
                  <Table.Th className='u-bg-gray-alt' key={key}>
                    {this.props.tabContent.tableCount.headerDisp[key]}
                  </Table.Th>
                );
              })}
            </Table.Tr>
          </Table.THead>
          <Table.TBody>
            {this.props.isLoading && (
              <Table.Tr className='report-table__loading-row'>
                <Table.Td colSpan={this.props.reportTableRow.tableRowTitle.header.length + this.props.tabContent.tableCount.header.length}>
                  <LinearProgress className='u-w-full' isIndeterminate />
                </Table.Td>
              </Table.Tr>
            )}
            {this.props.reportTableRow.displayKeyList.map((key: string) =>
              key === 'total' || key === 'customers' ? null : (
                <Table.Tr key={`table-row-${key}`}>
                  {this.props.reportTableRow.tableRowTitle.data[key].map((tableCol: string, i: number) => (
                    <Table.Td
                      key={`${key}-${i}`}
                      className={classNames(
                        'report-table__col-sticky',
                        `report-table__col-sticky--${i + 1}`,
                        'u-bg-white u-white-space-nowrap'
                      )}>
                      {tableCol}
                    </Table.Td>
                  ))}
                  {this.props.tabContent.tableCount.data[key].map((tableCol: number, i: number) => {
                    if (this.state.isPendingNegotiationActionTab && this.props.tabContent.tableCount.header[i] === ACTION_TYPE.NOT_ENTERED)
                      return null;
                    return (
                      <Table.Td key={`${key}-${i + 2}`} className='u-text-right'>
                        <Link
                          to={SCREEN.NEGOTIATION_SEARCH_RESULT.URL}
                          onClick={() =>
                            this.props.updateSearchCondition(
                              this.props.tabContent.tabkey.lineType,
                              key,
                              this.props.tabContent.tableCount.header[i]
                            )
                          }
                          className={tableCol === 0 ? 'u-text-disabled' : 'u-text-underline'}
                          style={{ pointerEvents: tableCol === 0 ? 'none' : 'auto' }}>
                          {tableCol}
                        </Link>
                      </Table.Td>
                    );
                  })}
                </Table.Tr>
              )
            )}
          </Table.TBody>
        </Table>
      </div>
    );
  }
}

export default CountTable;
